
import { head, isEmpty, empty, ofArray } from "../../fable_modules/fable-library-js.5.3.0/List.js";
import { fromParts } from "../../fable_modules/fable-library-js.5.3.0/Decimal.js";

/**
 * Returns the ordered list of preset unit options for a given activity type.
 * The first item is the default. Returns empty list for "custom" or unknown activities.
 */
export function unitsForActivity(activity) {
    const matchValue = activity.toLowerCase();
    switch (matchValue) {
        case "walking":
            return ofArray(["miles", "steps", "km", "minutes"]);
        case "running":
            return ofArray(["miles", "km", "minutes"]);
        case "cycling":
            return ofArray(["miles", "km", "minutes"]);
        case "swimming":
            return ofArray(["laps", "yards", "meters", "minutes"]);
        case "reading":
            return ofArray(["pages", "books", "chapters", "minutes"]);
        case "meditation":
            return ofArray(["minutes", "sessions"]);
        case "exercise":
            return ofArray(["minutes", "reps", "sets", "sessions"]);
        default:
            return empty();
    }
}

/**
 * Returns the default unit for an activity (the first preset option).
 * Falls back to "units" for custom or unknown activities.
 */
export function defaultUnitForActivity(activity) {
    const matchValue = unitsForActivity(activity);
    if (isEmpty(matchValue)) {
        return "units";
    }
    else {
        return head(matchValue);
    }
}

/**
 * Returns the daily sanity threshold for a given activity+unit combo.
 * Exceeding this value per day triggers a soft warning.
 * Returns generic fallback (10000) for unknown combos.
 */
export function sanityThreshold(activity, unit) {
    const matchValue = activity.toLowerCase();
    const matchValue_1 = unit.toLowerCase();
    let matchResult;
    switch (matchValue) {
        case "walking": {
            switch (matchValue_1) {
                case "miles": {
                    matchResult = 0;
                    break;
                }
                case "steps": {
                    matchResult = 1;
                    break;
                }
                case "km": {
                    matchResult = 2;
                    break;
                }
                case "minutes": {
                    matchResult = 3;
                    break;
                }
                default:
                    matchResult = 24;
            }
            break;
        }
        case "running": {
            switch (matchValue_1) {
                case "miles": {
                    matchResult = 4;
                    break;
                }
                case "km": {
                    matchResult = 5;
                    break;
                }
                case "minutes": {
                    matchResult = 6;
                    break;
                }
                default:
                    matchResult = 24;
            }
            break;
        }
        case "cycling": {
            switch (matchValue_1) {
                case "miles": {
                    matchResult = 7;
                    break;
                }
                case "km": {
                    matchResult = 8;
                    break;
                }
                case "minutes": {
                    matchResult = 9;
                    break;
                }
                default:
                    matchResult = 24;
            }
            break;
        }
        case "swimming": {
            switch (matchValue_1) {
                case "laps": {
                    matchResult = 10;
                    break;
                }
                case "yards": {
                    matchResult = 11;
                    break;
                }
                case "meters": {
                    matchResult = 12;
                    break;
                }
                case "minutes": {
                    matchResult = 13;
                    break;
                }
                default:
                    matchResult = 24;
            }
            break;
        }
        case "reading": {
            switch (matchValue_1) {
                case "pages": {
                    matchResult = 14;
                    break;
                }
                case "books": {
                    matchResult = 15;
                    break;
                }
                case "chapters": {
                    matchResult = 16;
                    break;
                }
                case "minutes": {
                    matchResult = 17;
                    break;
                }
                default:
                    matchResult = 24;
            }
            break;
        }
        case "meditation": {
            switch (matchValue_1) {
                case "minutes": {
                    matchResult = 18;
                    break;
                }
                case "sessions": {
                    matchResult = 19;
                    break;
                }
                default:
                    matchResult = 24;
            }
            break;
        }
        case "exercise": {
            switch (matchValue_1) {
                case "minutes": {
                    matchResult = 20;
                    break;
                }
                case "reps": {
                    matchResult = 21;
                    break;
                }
                case "sets": {
                    matchResult = 22;
                    break;
                }
                case "sessions": {
                    matchResult = 23;
                    break;
                }
                default:
                    matchResult = 24;
            }
            break;
        }
        default:
            matchResult = 24;
    }
    switch (matchResult) {
        case 0:
            return fromParts(6, 0, 0, false, 0);
        case 1:
            return fromParts(10000, 0, 0, false, 0);
        case 2:
            return fromParts(10, 0, 0, false, 0);
        case 3:
            return fromParts(60, 0, 0, false, 0);
        case 4:
            return fromParts(8, 0, 0, false, 0);
        case 5:
            return fromParts(13, 0, 0, false, 0);
        case 6:
            return fromParts(60, 0, 0, false, 0);
        case 7:
            return fromParts(30, 0, 0, false, 0);
        case 8:
            return fromParts(50, 0, 0, false, 0);
        case 9:
            return fromParts(90, 0, 0, false, 0);
        case 10:
            return fromParts(60, 0, 0, false, 0);
        case 11:
            return fromParts(1500, 0, 0, false, 0);
        case 12:
            return fromParts(1500, 0, 0, false, 0);
        case 13:
            return fromParts(60, 0, 0, false, 0);
        case 14:
            return fromParts(50, 0, 0, false, 0);
        case 15:
            return fromParts(1, 0, 0, false, 0);
        case 16:
            return fromParts(5, 0, 0, false, 0);
        case 17:
            return fromParts(60, 0, 0, false, 0);
        case 18:
            return fromParts(30, 0, 0, false, 0);
        case 19:
            return fromParts(2, 0, 0, false, 0);
        case 20:
            return fromParts(90, 0, 0, false, 0);
        case 21:
            return fromParts(200, 0, 0, false, 0);
        case 22:
            return fromParts(15, 0, 0, false, 0);
        case 23:
            return fromParts(2, 0, 0, false, 0);
        default:
            return fromParts(10000, 0, 0, false, 0);
    }
}

/**
 * Returns the typical range text for a known activity+unit combo.
 * Returns None for unknown or custom combos.
 */
export function typicalRange(activity, unit) {
    const matchValue = activity.toLowerCase();
    const matchValue_1 = unit.toLowerCase();
    let matchResult;
    switch (matchValue) {
        case "walking": {
            switch (matchValue_1) {
                case "miles": {
                    matchResult = 0;
                    break;
                }
                case "steps": {
                    matchResult = 1;
                    break;
                }
                case "km": {
                    matchResult = 2;
                    break;
                }
                case "minutes": {
                    matchResult = 3;
                    break;
                }
                default:
                    matchResult = 24;
            }
            break;
        }
        case "running": {
            switch (matchValue_1) {
                case "miles": {
                    matchResult = 4;
                    break;
                }
                case "km": {
                    matchResult = 5;
                    break;
                }
                case "minutes": {
                    matchResult = 6;
                    break;
                }
                default:
                    matchResult = 24;
            }
            break;
        }
        case "cycling": {
            switch (matchValue_1) {
                case "miles": {
                    matchResult = 7;
                    break;
                }
                case "km": {
                    matchResult = 8;
                    break;
                }
                case "minutes": {
                    matchResult = 9;
                    break;
                }
                default:
                    matchResult = 24;
            }
            break;
        }
        case "swimming": {
            switch (matchValue_1) {
                case "laps": {
                    matchResult = 10;
                    break;
                }
                case "yards": {
                    matchResult = 11;
                    break;
                }
                case "meters": {
                    matchResult = 12;
                    break;
                }
                case "minutes": {
                    matchResult = 13;
                    break;
                }
                default:
                    matchResult = 24;
            }
            break;
        }
        case "reading": {
            switch (matchValue_1) {
                case "pages": {
                    matchResult = 14;
                    break;
                }
                case "books": {
                    matchResult = 15;
                    break;
                }
                case "chapters": {
                    matchResult = 16;
                    break;
                }
                case "minutes": {
                    matchResult = 17;
                    break;
                }
                default:
                    matchResult = 24;
            }
            break;
        }
        case "meditation": {
            switch (matchValue_1) {
                case "minutes": {
                    matchResult = 18;
                    break;
                }
                case "sessions": {
                    matchResult = 19;
                    break;
                }
                default:
                    matchResult = 24;
            }
            break;
        }
        case "exercise": {
            switch (matchValue_1) {
                case "minutes": {
                    matchResult = 20;
                    break;
                }
                case "reps": {
                    matchResult = 21;
                    break;
                }
                case "sets": {
                    matchResult = 22;
                    break;
                }
                case "sessions": {
                    matchResult = 23;
                    break;
                }
                default:
                    matchResult = 24;
            }
            break;
        }
        default:
            matchResult = 24;
    }
    switch (matchResult) {
        case 0:
            return "3–6 mi/day";
        case 1:
            return "5k–10k steps/day";
        case 2:
            return "5–10 km/day";
        case 3:
            return "30–60 min/day";
        case 4:
            return "3–8 mi/day";
        case 5:
            return "5–13 km/day";
        case 6:
            return "30–60 min/day";
        case 7:
            return "10–30 mi/day";
        case 8:
            return "15–50 km/day";
        case 9:
            return "30–90 min/day";
        case 10:
            return "20–60 laps/day";
        case 11:
            return "500–1,500 yd/day";
        case 12:
            return "500–1,500 m/day";
        case 13:
            return "30–60 min/day";
        case 14:
            return "20–50 pages/day";
        case 15:
            return "0.5–1 book/day";
        case 16:
            return "2–5 chapters/day";
        case 17:
            return "30–60 min/day";
        case 18:
            return "10–30 min/day";
        case 19:
            return "1–2 sessions/day";
        case 20:
            return "30–90 min/day";
        case 21:
            return "50–200 reps/day";
        case 22:
            return "5–15 sets/day";
        case 23:
            return "1–2 sessions/day";
        default:
            return undefined;
    }
}

//# sourceMappingURL=UnitConfig.js.map
